/* ==========================================================================
   CADOO SHOPPING CONTEXT

   The smallest reliable record of what the customer has EXPLICITLY told us
   they are shopping for, carried from a discovery surface through to the
   moment they add something to the bag.

   WHAT THIS IS NOT
   This is not product metadata and it is not an approval list. Three separate
   ideas are deliberately kept apart across this theme:

     1. Approved compatibility  - custom.compatible_add_ons on the main gift.
                                  The only thing that may introduce a product.
     2. Product eligibility     - custom.occasion / custom.recipient on the
                                  add-on. What that add-on is suitable for.
     3. Shopper intent          - THIS FILE. What the customer actually chose.

   Intent can only ever narrow an already-approved list. It can never add to it.

   STORAGE
   sessionStorage, one versioned key, tab-scoped, so a shopping journey cannot
   leak into a different tab or survive the browser being closed. localStorage
   is deliberately NOT used: a stale "Birthday" from three weeks ago is exactly
   the failure this system exists to avoid. A timestamp backstop expires a
   dimension that has been sitting in a tab left open overnight.

   HANDLES, NOT IDS
   Values are metaobject handles - the stable identifier a human sets in admin.
   No GID is ever stored, so an entry that is deleted and recreated under the
   same handle keeps working with no edit anywhere.

   CAPTURE POINTS (both are destinations, not links)
   - Filtered collection pages. snippets/cadoo-context-active.liquid emits the
     dimensions that page can filter on, and which values are active, resolved
     to handles server-side from Shopify's own collection.filters. That single
     point covers occasion navigation, the mega menu, hub tiles, the filter UI,
     a shared link, and Quick Add performed directly on filtered results -
     because the context is already recorded before the customer clicks
     anything on that page.
   - The Gift Finder. assets/cadoo-gift-finder.js records recipient and
     interest as they are chosen, and clears them on "No preference" or a
     restart.

   AUTHORITATIVE PAGE SEMANTICS
   A collection page that exposes a Cadoo dimension as a filter is treated as
   the truth for that dimension: whatever is active becomes the context, and
   whatever is not active is cleared. That is what makes "clears a filter" and
   "navigates to an unfiltered discovery state" work without a special case for
   either, and it means the recorded context always matches what the customer
   is actually looking at. Pages that do not expose a dimension leave it alone,
   so reaching a product page never clears anything.

   FAILURE
   Every read and write is guarded. Storage being unavailable (private mode,
   blocked site data), malformed JSON, an unknown version and an expired
   record all resolve to "no context known", which the recommendation rules
   already treat as fail-closed for occasion-specific add-ons.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'cadoo_shopping_context_v1';
  var VERSION = 1;
  var MAX_AGE_MS = 6 * 60 * 60 * 1000;
  var MAX_VALUES = 12;
  var MAX_VALUE_LEN = 80;

  /* The four Cadoo taxonomy dimensions. Matches the metaobject types read by
     snippets/cadoo-taxonomy.liquid and the filter keys built by
     snippets/cadoo-filter-link.liquid, so nothing here needs editing when a
     value is added, renamed or retired in admin. */
  var DIMENSIONS = ['occasion', 'recipient', 'interest', 'gift_category'];

  var available = (function () {
    try {
      var s = window.sessionStorage;
      if (!s) return false;
      var probe = KEY + '__probe';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();

  function isDimension(dim) {
    return DIMENSIONS.indexOf(dim) > -1;
  }

  /* A metaobject handle is lowercase alphanumerics with hyphens or
     underscores. Anything else is not a handle we could have written, so it is
     dropped rather than trusted. */
  function normalizeValue(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '');
  }

  function normalizeList(values) {
    var raw = Array.isArray(values) ? values : String(values == null ? '' : values).split(',');
    var out = [];
    var seen = Object.create(null);
    for (var i = 0; i < raw.length; i++) {
      var v = normalizeValue(raw[i]);
      if (!v || v.length > MAX_VALUE_LEN || seen[v]) continue;
      seen[v] = true;
      out.push(v);
      if (out.length >= MAX_VALUES) break;
    }
    return out;
  }

  function wipe() {
    if (!available) return;
    try { window.sessionStorage.removeItem(KEY); } catch (e) {}
  }

  function readRecord() {
    if (!available) return null;
    var text = null;
    try { text = window.sessionStorage.getItem(KEY); } catch (e) { return null; }
    if (!text) return null;

    var data = null;
    try { data = JSON.parse(text); } catch (e) { data = null; }

    if (!data || typeof data !== 'object' || data.v !== VERSION ||
        !data.dims || typeof data.dims !== 'object') {
      wipe();
      return null;
    }
    return data;
  }

  function writeRecord(record) {
    if (!available) return;
    var keys = Object.keys(record.dims);
    if (!keys.length) { wipe(); return; }
    try { window.sessionStorage.setItem(KEY, JSON.stringify(record)); } catch (e) {}
  }

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function entryIsFresh(entry) {
    if (!entry || !Array.isArray(entry.values) || !entry.values.length) return false;
    var at = Number(entry.at);
    if (!at || isNaN(at)) return false;
    var age = now() - at;
    return age >= 0 && age <= MAX_AGE_MS;
  }

  /* Returns { occasion: ['birthday'], recipient: [...] } holding only
     dimensions that are set, valid and unexpired. Expired entries are pruned
     from storage as they are read, so a stale record cleans itself up. */
  function get() {
    var record = readRecord();
    var out = {};
    if (!record) return out;

    var changed = false;
    for (var i = 0; i < DIMENSIONS.length; i++) {
      var dim = DIMENSIONS[i];
      var entry = record.dims[dim];
      if (!entry) continue;
      if (entryIsFresh(entry)) {
        out[dim] = normalizeList(entry.values);
        if (!out[dim].length) { delete out[dim]; delete record.dims[dim]; changed = true; }
      } else {
        delete record.dims[dim];
        changed = true;
      }
    }
    if (changed) writeRecord(record);
    return out;
  }

  function getDimension(dim) {
    if (!isDimension(dim)) return [];
    return get()[dim] || [];
  }

  /* Records an explicit choice. An empty list clears that dimension, which is
     what "No preference", "Any" and a removed filter all resolve to. */
  function set(dim, values, source) {
    if (!available || !isDimension(dim)) return;
    var list = normalizeList(values);
    var record = readRecord() || { v: VERSION, dims: {} };

    if (!list.length) {
      if (!record.dims[dim]) return;
      delete record.dims[dim];
    } else {
      record.dims[dim] = {
        values: list,
        source: String(source || 'unknown').slice(0, 40),
        at: now()
      };
    }
    writeRecord(record);
  }

  function clear(dim) {
    set(dim, [], 'clear');
  }

  function clearAll() {
    wipe();
  }

  /* Applies one page's authoritative view of the dimensions it can filter on.
     `dims` is the list of dimensions this page exposes as filters; `values` is
     a map of the ones that are actually active. Any exposed dimension with no
     active value is cleared, which is how removing a filter chip and landing
     on an unfiltered discovery state both take effect without a special case.
     Dimensions the page does not expose are left untouched. */
  function applyPageState(dims, values, source) {
    if (!available) return;
    var exposed = Array.isArray(dims) ? dims : String(dims == null ? '' : dims).split(',');
    for (var i = 0; i < exposed.length; i++) {
      var dim = String(exposed[i] || '').trim();
      if (!isDimension(dim)) continue;
      set(dim, (values && values[dim]) || [], source || 'page');
    }
  }

  /* Reads the server-rendered marker a discovery page emits and applies it.
     See snippets/cadoo-context-active.liquid. */
  function hydrateFromDocument() {
    if (!available) return;
    var el = document.querySelector('[data-cd-ctx-source]');
    if (!el) return;

    var dims = (el.getAttribute('data-cd-ctx-dims') || '').split(',');
    var values = {};
    for (var i = 0; i < dims.length; i++) {
      var dim = String(dims[i] || '').trim();
      if (!isDimension(dim)) continue;
      values[dim] = normalizeList(el.getAttribute('data-cd-ctx-' + dim) || '');
    }
    applyPageState(dims, values, el.getAttribute('data-cd-ctx-source') || 'page');
  }

  window.CadooContext = {
    available: available,
    dimensions: DIMENSIONS.slice(),
    get: get,
    getDimension: getDimension,
    set: set,
    clear: clear,
    clearAll: clearAll,
    applyPageState: applyPageState
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateFromDocument);
  } else {
    hydrateFromDocument();
  }
})();
